import React, { useRef, useState } from 'react'
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import CardContent from '@mui/material/CardContent'
import FormControl from '@mui/material/FormControl'
import FormGroup from '@mui/material/FormGroup'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import MailList from './MailList'
import useStyles from './styles/establishmentFormStyles'
import TokTokDocTextField from '../Common/TextField'
import { isEmailValid, isPhoneValid, isPostalCodeValid } from '../../utils/validators'
import TokTokDocSaveButtons from '../Common/SaveButtons'
import { useAppDispatch } from '../../store'
import { EstablishmentEmailsKeys, EstablishmentFormProps, EstablishmentEmails } from './_types'
import useUserPermissions from '../../hooks/useUserPermissions'
import {
    getGetIdsHealthcareservicesHcsIdDetailsQueryKey,
    UpdateHealthCareServiceBodyIds,
    usePutIdsHealthcareservicesHcsId,
} from '@toktokdoctech/tokapi-client'
import { displaySnackbar } from '../../actions/main'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'

export const EstablishmentForm = ({ initialEstablishmentData: hcs }: EstablishmentFormProps) => {
    const dispatch = useAppDispatch()
    const classes = useStyles()
    const navigate = useNavigate()

    const { hasUserPermission } = useUserPermissions()
    const [isDirty, setIsDirty] = React.useState(false)

    const street = useRef<HTMLInputElement>()
    const city = useRef<HTMLInputElement>()
    const postalCode = useRef<HTMLInputElement>()
    const phone = useRef<HTMLInputElement>()
    const [emails, setEmails] = useState<EstablishmentEmails>({
        contactEmails: hcs.contactEmails,
        orgEmails: hcs.orgEmails,
        dirEmails: hcs.dirEmails,
        delegateEmails: hcs.delegateEmails,
    })

    const queryClient = useQueryClient()

    const { mutate } = usePutIdsHealthcareservicesHcsId({
        mutation: {
            onSuccess: async () => {
                setIsDirty(false)
                const queryKey = getGetIdsHealthcareservicesHcsIdDetailsQueryKey(hcs.id)
                await queryClient.invalidateQueries({ queryKey })
                dispatch(
                    displaySnackbar(
                        'success',
                        'Votre modification a bien été enregistrée !',
                        undefined,
                        `[EstablishmentForm] hcs ${hcs.id} successfully updated`,
                    ),
                )
                navigate('/')
            },
            onError: (err) => {
                const error = err instanceof Error ? err : String(err)
                const errorMessage = `[EstablishmentForm] hcs ${hcs.id} failed to update: ${error}`
                console.error(errorMessage)
                setIsDirty(false)
                dispatch(
                    displaySnackbar(
                        'error',
                        'Une erreur est survenue lors de la modification.',
                        undefined,
                        errorMessage,
                    ),
                )
            },
        },
    })

    const addEmail = (key: EstablishmentEmailsKeys, value: string) => {
        if (emails[key]?.includes(value) === false) {
            emails[key]?.unshift(value)
            setIsDirty(true)
            setEmails({
                ...emails,
                [key]: emails[key],
            })
        } else throw new Error(`[addEmail] wrong email key: ${key}, expected keys: ${Object.keys(emails).join()}`)
    }

    const removeEmail = (key: EstablishmentEmailsKeys, value: string) => {
        const index = emails[key]?.indexOf(value)
        if (Number.isNaN(index)) {
            return
        }
        emails[key]?.splice(Number(index), 1)
        setIsDirty(true)
        setEmails({
            ...emails,
            [key]: emails[key],
        })
    }

    const contactHasChanged =
        hcs.address.street !== street.current?.value ||
        hcs.address.city !== city.current?.value ||
        hcs.address.postalCode !== postalCode.current?.value ||
        hcs.phone !== phone.current?.value

    const formatData = (): UpdateHealthCareServiceBodyIds => ({
        address: {
            street: street.current?.value ?? '',
            city: city.current?.value ?? '',
            postalCode: postalCode.current?.value ?? '',
        },
        phone: phone.current?.value ?? '',
        contactEmails: emails.contactEmails,
        orgEmails: emails.orgEmails,
        dirEmails: emails.dirEmails,
        delegatedEmails: emails.delegateEmails,
    })

    const dataValidator = (data: UpdateHealthCareServiceBodyIds) => {
        if (
            data.address.street !== '' &&
            data.address.city !== '' &&
            data.contactEmails &&
            data.contactEmails.length > 0 &&
            data.delegatedEmails &&
            data.delegatedEmails.length > 0 &&
            isPostalCodeValid(data.address?.postalCode) === true &&
            isPhoneValid(data.phone) === true
        ) {
            return true
        }
        return 'Veuillez corriger tous les champs comportant des erreurs pour pouvoir enregistrer.'
    }

    const handleSave = async () => {
        if (hasUserPermission('ADMIN')) {
            const data = formatData()
            const result = dataValidator(data)
            if (result === true) {
                mutate({
                    hcsId: hcs.id,
                    data,
                })
            }
        } else {
            throw new Error('Vous ne disposez pas des droits suffisants pour effectuer cette opération !')
        }
    }

    const renderEstablishmentManager = () => (
        <div className={classes.root}>
            <Grid container justifyContent="center">
                <Grid item xs={12} className={classes.topCard}>
                    <Paper className={classes.paper}>
                        <Card className={`no-padding-bottom-card ${classes.root}`}>
                            <CardContent>
                                <FormControl variant="outlined" component="fieldset" className={classes.topCardForm}>
                                    <FormGroup>
                                        <Grid container justifyContent="center">
                                            <Grid item xs={12} md={6} className={classes.gridItemsContainerLeft}>
                                                <div className={classes.gridItemsDiv}>
                                                    <TextField
                                                        variant="outlined"
                                                        autoComplete="none"
                                                        className={classes.gridItemContent}
                                                        id="name"
                                                        label="Nom"
                                                        value={hcs.name}
                                                        disabled
                                                    />
                                                    <TokTokDocTextField
                                                        autoComplete="none"
                                                        className={classes.gridItemContent}
                                                        inputRef={street}
                                                        id="street"
                                                        label="Adresse *"
                                                        defaultValue={hcs.address.street}
                                                        defaultState={!hcs.address.street ? 'ERROR' : undefined}
                                                        validator={(val: string) =>
                                                            val !== '' || 'Une adresse doit être fournie'
                                                        }
                                                    />
                                                    <TextField
                                                        variant="outlined"
                                                        className={classes.gridItemContent}
                                                        select
                                                        id="sector"
                                                        label="Secteur"
                                                        value="sector"
                                                        disabled
                                                    >
                                                        <MenuItem value="sector">{hcs.sector?.name}</MenuItem>
                                                    </TextField>
                                                    <TextField
                                                        variant="outlined"
                                                        className={classes.gridItemContent}
                                                        select
                                                        id="price"
                                                        label="Tarification"
                                                        value="tarification"
                                                        disabled
                                                    >
                                                        <MenuItem value="tarification">{hcs.payModel?.name}</MenuItem>
                                                    </TextField>
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} md={6} className={classes.gridItemsContainerRight}>
                                                <div className={classes.gridItemsDiv}>
                                                    <TokTokDocTextField
                                                        autoComplete="none"
                                                        className={classes.gridItemContent}
                                                        inputRef={phone}
                                                        id="phoneNumber"
                                                        label="Téléphone *"
                                                        defaultValue={hcs.phone}
                                                        defaultState={!hcs.phone ? 'ERROR' : undefined}
                                                        validator={isPhoneValid}
                                                    />
                                                    <Grid container>
                                                        <Grid item xs={5} sm={4}>
                                                            <TokTokDocTextField
                                                                autoComplete="none"
                                                                className={classes.gridItemContent}
                                                                inputRef={postalCode}
                                                                id="postalCode"
                                                                label="Code Postal *"
                                                                defaultValue={hcs.address?.postalCode}
                                                                defaultState={
                                                                    !hcs.address?.postalCode ? 'ERROR' : undefined
                                                                }
                                                                validator={isPostalCodeValid}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={7} sm={8}>
                                                            <TokTokDocTextField
                                                                autoComplete="none"
                                                                className={classes.gridItemContent}
                                                                inputRef={city}
                                                                id="city"
                                                                label="Ville *"
                                                                defaultValue={hcs.address?.city}
                                                                defaultState={!hcs.address?.city ? 'ERROR' : undefined}
                                                                validator={(val: string) =>
                                                                    val !== '' || 'Une ville doit être fournie'
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                    <TextField
                                                        variant="outlined"
                                                        className={classes.gridItemContent}
                                                        select
                                                        id="type"
                                                        label="Type"
                                                        value="type"
                                                        disabled
                                                    >
                                                        <MenuItem value="type">{hcs.type?.name}</MenuItem>
                                                    </TextField>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </FormGroup>
                                </FormControl>
                            </CardContent>
                        </Card>
                    </Paper>
                </Grid>
                <Grid item xs={12} className={classes.bottomCard}>
                    <Paper className={classes.paper}>
                        <Card className={`no-padding-bottom-card ${classes.root}`}>
                            <CardContent>
                                <FormControl variant="outlined" component="fieldset">
                                    <FormGroup>
                                        <Grid container justifyContent="center">
                                            <Grid item xs={12} className={classes.gridItemsContainerLeft}>
                                                <div className={classes.gridItemsDiv}>
                                                    <div className={classes.gridItemContent}>
                                                        <Typography variant="h5">
                                                            Gestion des courriels de l'établissement
                                                        </Typography>
                                                    </div>
                                                    <div className={classes.gridItemContent}>
                                                        <Typography variant="h6">
                                                            Pour ajouter un courriel, placez-vous sur la section du type
                                                            de courriel à ajouter et saisissez l'adresse du nouveau
                                                            courriel. Pour valider votre saisie, tapez sur la touche
                                                            "Entrée", votre nouveau courriel apparaît dans la liste.
                                                        </Typography>
                                                    </div>
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} md={6} className={classes.gridItemsContainerLeft}>
                                                <div className={classes.gridItemsDiv}>
                                                    <Card className={classes.mailCard}>
                                                        <TokTokDocTextField
                                                            autoComplete="none"
                                                            className={classes.gridItemContent}
                                                            id="contactEmails"
                                                            label="Courriels de contact *"
                                                            defaultValue=""
                                                            defaultState={
                                                                emails.contactEmails.length === 0 ? 'ERROR' : undefined
                                                            }
                                                            validator={isEmailValid}
                                                            callback={addEmail}
                                                            tooltipText="Réception de courriels informatifs sur les produits et services de TokTokDoc."
                                                            noMargin
                                                        />
                                                        <MailList
                                                            id="contactEmails"
                                                            mails={emails.contactEmails}
                                                            required
                                                            callback={removeEmail}
                                                        />
                                                    </Card>
                                                    <Card className={classes.mailCard}>
                                                        <TokTokDocTextField
                                                            autoComplete="none"
                                                            className={classes.gridItemContent}
                                                            id="delegateEmails"
                                                            label="Courriels administratifs *"
                                                            defaultValue=""
                                                            defaultState={
                                                                emails.delegateEmails.length === 0 ? 'ERROR' : undefined
                                                            }
                                                            validator={isEmailValid}
                                                            callback={addEmail}
                                                            tooltipText="Réception des documents administratifs (notamment feuilles de soins et notes d'honoraires)."
                                                            noMargin
                                                        />
                                                        <MailList
                                                            id="delegateEmails"
                                                            mails={emails.delegateEmails}
                                                            required
                                                            callback={removeEmail}
                                                        />
                                                    </Card>
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} md={6} className={classes.gridItemsContainerRight}>
                                                <div className={classes.gridItemsDiv}>
                                                    <Card className={classes.mailCard}>
                                                        <TokTokDocTextField
                                                            autoComplete="none"
                                                            className={classes.gridItemContent}
                                                            id="orgEmails"
                                                            label="Courriels de l'équipe de soin"
                                                            defaultValue=""
                                                            validator={isEmailValid}
                                                            callback={addEmail}
                                                            tooltipText="Réception des documents médicaux (notamment comptes-rendus et ordonnances)."
                                                            noMargin
                                                        />
                                                        <MailList
                                                            id="orgEmails"
                                                            mails={emails.orgEmails}
                                                            callback={removeEmail}
                                                        />
                                                    </Card>
                                                    <Card className={classes.mailCard}>
                                                        <TokTokDocTextField
                                                            autoComplete="none"
                                                            className={classes.gridItemContent}
                                                            id="dirEmails"
                                                            label="Courriels de la direction"
                                                            defaultValue=""
                                                            validator={isEmailValid}
                                                            callback={addEmail}
                                                            tooltipText="Réception de courriels techniques ou contractuels."
                                                            noMargin
                                                        />
                                                        <MailList
                                                            id="dirEmails"
                                                            mails={emails.dirEmails}
                                                            callback={removeEmail}
                                                        />
                                                    </Card>
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </FormGroup>
                                </FormControl>
                            </CardContent>
                        </Card>
                    </Paper>
                </Grid>
            </Grid>
        </div>
    )

    return (
        <>
            {/* @ts-expect-error TS(2339) FIXME: Property 'EstablishmentContent' does not exist on ... Remove this comment to see the full error message */}
            <div className={classes.EstablishmentContent}>
                {/* @ts-expect-error TS(2339) FIXME: Property 'EstablishmentContentItem' does not exist... Remove this comment to see the full error message */}
                <div className={classes.EstablishmentContentItem}>{renderEstablishmentManager()}</div>
            </div>
            <div className={classes.EstablishmentButtons}>
                <TokTokDocSaveButtons
                    allowCancel
                    cancelRedirectTo="/"
                    onSave={handleSave}
                    shouldBlockNavigation={() => isDirty || contactHasChanged}
                />
            </div>
        </>
    )
}
